import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchCollection } from '../lib/cms';
import { Star, Quote, ShieldCheck, ArrowUpRight, ExternalLink } from 'lucide-react';

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTestimonials() {
      const data = await fetchCollection('testimonials', { sort: 'date', order: 'desc' });
      if (data && data.items) {
        setTestimonials(data.items);
      }
      setLoading(false);
    }
    loadTestimonials();
  }, []);

  const metaTitle = "Anbefalinger - KBS Tømrer – Montage ApS";
  const metaDesc = "Læs anmeldelser fra vores tilfredse kunder i Malling, Odder, Århus og resten af Østjylland. Vi er stolte af vores topkarakterer hos 3byg.dk.";

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <html lang="da" />
      </Helmet>

      {/* Hero Header */}
      <section className="relative bg-slate-950 text-white py-20 lg:py-28">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/10033775-ad6c-49dd-a719-01817fe062a0/baggrund-1000.jpg"
            alt="KBS Tømrer Anbefalinger"
            className="h-full w-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-slate-950 to-slate-950" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Kundeanbefalinger
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Læs hvad vores kunder siger om vores arbejde. Vi sætter en ære i at levere et stykke arbejde, der overstiger dine forventninger.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white flex-1">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Column: Testimonials List */}
            <div className="lg:col-span-8 space-y-8">
              {testimonials.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  Der blev ikke fundet nogen anmeldelser.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  {testimonials.map((item) => (
                    <div
                      key={item.id}
                      className="relative rounded-2xl border border-slate-200 bg-slate-50/30 p-6 sm:p-8 shadow-sm hover:shadow-md transition-all"
                    >
                      <Quote className="absolute top-6 right-6 h-12 w-12 text-slate-100 shrink-0 pointer-events-none" />
                      
                      <div className="flex items-center gap-1 text-brand-gold mb-4">
                        {[...Array(item.rating)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 fill-current" />
                        ))}
                      </div>

                      <div
                        className="text-slate-600 text-base leading-relaxed mb-6 italic"
                        dangerouslySetInnerHTML={{ __html: item.content.da }}
                      />

                      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-4">
                        <div>
                          <h4 className="font-display font-bold text-brand-charcoal text-base">
                            {item.author.da}
                          </h4>
                          <p className="text-xs text-slate-400 mt-0.5">
                            Verificeret kundeanmeldelse
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-semibold text-brand-primary bg-brand-primary/5 border border-brand-primary/10 px-3 py-1 rounded-full">
                            Anbefalet via {item.source || '3byg.dk'}
                          </span>
                          <span className="text-xs text-slate-400">
                            {new Date(item.date).toLocaleDateString('da-DK', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column: 3byg Trust Badge & Sidebar */}
            <div className="lg:col-span-4">
              <div className="sticky top-28 space-y-8">
                {/* 3byg Trust Card */}
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8 shadow-md text-center">
                  <div className="mx-auto bg-brand-primary text-white rounded-2xl p-4 w-fit mb-6 shadow-md">
                    <ShieldCheck className="h-10 w-10" />
                  </div>
                  
                  <h3 className="font-display text-xl font-bold text-brand-charcoal mb-3">
                    Medlem af 3byg.dk
                  </h3>
                  
                  <p className="text-sm text-slate-500 leading-relaxed mb-6">
                    KBS Tømrer – Montage ApS er stolt medlem af 3byg, hvor vi løbende modtager fremragende anmeldelser for vores tømrerarbejde, pålidelighed og service.
                  </p>

                  <div className="rounded-xl bg-white border border-slate-200 p-4 mb-6">
                    <div className="flex items-center justify-center gap-1 text-brand-gold mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-current" />
                      ))}
                    </div>
                    <p className="text-xs font-bold text-brand-charcoal">5.0 ud af 5.0 stjerner</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Baseret på verificerede kundesager</p>
                  </div>

                  <a
                    href="https://www.3byg.dk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand-primary hover:bg-brand-primary-dark px-4 py-3.5 text-sm font-bold text-white shadow hover:shadow-md transition-all cursor-pointer"
                  >
                    Se vores profil på 3byg <ExternalLink className="h-4 w-4" />
                  </a>
                </div>

                {/* Quality Guarantee */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-md">
                  <h3 className="font-display text-base font-bold text-brand-charcoal mb-3">
                    Vores kvalitetsgaranti
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Vi forlader aldrig en opgave, før du som kunde er 100% tilfreds. Vores høje moral, ærlighed og gode kommunikation sikrer dig et trygt byggeforløb fra start til slut.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
